import { useState, useEffect, useMemo } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { Flex, Spin, Typography } from 'antd';
import styled from 'styled-components';

// 组件导入
import Overview from './comps/Overview';
import UTXO from './comps/UTXO';
import { getTransactionById, ITransaction } from '@/api/commitment';
import { SupportedChain, SupportedChains } from '@packet/constants/AppSupportChains';
import { Hash } from 'viem';
import { parseTransactionHash } from '@/utils/transaction';

export default function TransactionDetail() {
  const { chainId, txhash } = useParams<{ chainId: string; txhash: string }>();
  const [tx_hash, tx_index] = parseTransactionHash(txhash || '');
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const [transactionData, setTransactionData] = useState<ITransaction | null>(null);

  const chain: SupportedChain | undefined = SupportedChains.find((c) => c.id === Number(chainId));

  // 数据加载
  useEffect(() => {
    const loadTransactionData = async () => {
      if (!chain) return;
      setLoading(true);

      try {
        const res = await getTransactionById(tx_hash as Hash, tx_index);
        if (res) setTransactionData(res);
      } finally {
        setLoading(false);
      }
    };

    if (tx_hash && tx_index !== -1) {
      loadTransactionData();
    }
  }, [tx_hash, tx_index, navigate, chain]);

  if (!chain) {
    return <Navigate to="/transactions" />;
  }

  if (!tx_hash || tx_index === -1) {
    return <Navigate to="/transactions" />;
  }

  if (loading) {
    return (
      <PageContainer>
        <Flex align="center" justify="center" style={{ height: '30vh' }}>
          <Spin />
        </Flex>
      </PageContainer>
    );
  }

  if (!transactionData) {
    return (
      <Flex align="center" justify="center" style={{ height: '30vh' }}>
        <Typography.Text>Transaction not found</Typography.Text>
      </Flex>
    );
  }

  // 转换UTXO数据
  const inputUTXOs = (transactionData.inputs || []).map((input) => ({
    ...input,
    id: input.commitment,
    chainId: chain?.id!,
    amount: input.amount,
  }));

  const outputUTXOs = (transactionData.outputs || []).map((output) => ({
    ...output,
    id: output.commitment,
    chainId: chain?.id!,
    amount: output.amount,
  }));

  return (
    <PageContainer>
      <ContentWrapper>
        <PageTitle>Transaction Details</PageTitle>

        {/* Overview 组件 */}
        <Overview transactionHash={txhash!} transaction={transactionData} chain={chain} />

        <Divider />

        {/* UTXO 组件 */}
        <UTXO inputs={inputUTXOs} outputs={outputUTXOs} />
      </ContentWrapper>
    </PageContainer>
  );
}

// Styled Components
const PageContainer = styled.div`
  width: 100%;
  max-width: 1440px;
  padding: 0 40px;
  margin: 0 auto;
  padding-bottom: 80px;
  background: #ffffff;
  min-height: calc(100vh - var(--pc-header-height));

  @media (max-width: 1024px) {
    padding: 0 40px;
  }

  @media (max-width: 768px) {
    padding: 0 16px;
    padding-bottom: 40px;
  }
`;

const PageTitle = styled.h1`
  font-family: var(--font-primary);
  font-size: 24px;
  font-weight: 600;
  color: var(--color-text);
  margin: 36px 0 24px 0;
  text-align: left;

  @media (max-width: 768px) {
    font-size: 24px;
    margin: 24px 0 16px 0;
  }
`;

const ContentWrapper = styled.div`
  width: 100%;
  max-width: 1280px;
  margin: 0 auto;

  @media (max-width: 768px) {
    max-width: 100%;
  }
`;

const Divider = styled.div`
  width: 100%;
  height: 1px;
  background-color: var(--color-text-01);
  margin: 48px 0;

  @media (max-width: 768px) {
    margin: 32px 0;
  }
`;
